import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Card, CardBody } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import CuratedSourceFormModal from '@/components/admin/CuratedSourceFormModal'
import { curatedSourceService, CuratedSource } from '@/services/curatedSourceService'
import { useToast } from '@/contexts/ToastContext'

const AdminCuratedSourcesPage: React.FC = () => {
  const { showToast } = useToast()

  const [sources, setSources] = useState<CuratedSource[]>([])
  const [loading, setLoading] = useState(true)
  const [formTarget, setFormTarget] = useState<CuratedSource | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)

  const fetchingRef = useRef(false)

  const fetchSources = useCallback(async () => {
    if (fetchingRef.current) return

    try {
      fetchingRef.current = true
      setLoading(true)
      const result = await curatedSourceService.listSources()
      setSources(result)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to fetch curated sources'
      })
    } finally {
      setLoading(false)
      fetchingRef.current = false
    }
  }, [showToast])

  useEffect(() => {
    fetchSources()
  }, [fetchSources])

  const handleOpenCreate = () => {
    setFormTarget(null)
    setIsFormOpen(true)
  }

  const handleOpenEdit = (source: CuratedSource) => {
    setFormTarget(source)
    setIsFormOpen(true)
  }

  const handleSaved = (saved: CuratedSource) => {
    setSources((prev) => {
      const exists = prev.some((s) => s.id === saved.id)
      return exists ? prev.map((s) => (s.id === saved.id ? saved : s)) : [saved, ...prev]
    })
  }

  const handleToggleEnabled = async (source: CuratedSource) => {
    try {
      const updated = await curatedSourceService.setEnabled(source.id, !source.enabled)
      setSources((prev) => prev.map((s) => (s.id === updated.id ? updated : s)))
      showToast({ type: 'success', message: updated.enabled ? 'Source enabled' : 'Source disabled' })
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update source'
      })
    }
  }

  const handleDelete = async (source: CuratedSource) => {
    if (!window.confirm(`Permanently delete "${source.name}"? This cannot be undone.`)) {
      return
    }

    try {
      await curatedSourceService.deleteSource(source.id)
      setSources((prev) => prev.filter((s) => s.id !== source.id))
      showToast({ type: 'success', message: 'Source deleted' })
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to delete source'
      })
    }
  }

  const formatHealth = (source: CuratedSource) => {
    const { consecutiveFailures, lastError, lastSuccessAt } = source.health

    if (consecutiveFailures > 0) {
      return (
        <span className="text-red-600">
          {consecutiveFailures} consecutive failure{consecutiveFailures === 1 ? '' : 's'}
          {lastError ? ` — ${lastError}` : ''}
        </span>
      )
    }

    if (lastSuccessAt) {
      return <span className="text-green-600">Healthy — last polled {new Date(lastSuccessAt).toLocaleString()}</span>
    }

    return <span className="text-gray-500">Not yet polled</span>
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Curated Sources</h2>
          <p className="mt-2 text-gray-600">Manage RSS/Atom sources feeding the curated News tab.</p>
        </div>
        <Button variant="primary" onClick={handleOpenCreate}>
          Add Source
        </Button>
      </div>

      <Card className="mt-6">
        <CardBody>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
            </div>
          ) : sources.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No curated sources found.</p>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200 text-sm text-gray-500">
                  <th className="pb-2">Name</th>
                  <th className="pb-2">Topic</th>
                  <th className="pb-2">Status</th>
                  <th className="pb-2">Health</th>
                  <th className="pb-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((source) => (
                  <tr key={source.id} className="border-b border-gray-100 last:border-0">
                    <td className="py-3">
                      <div className="font-medium text-gray-900">{source.name}</div>
                      <div className="text-sm text-gray-500">{source.feedUrl}</div>
                    </td>
                    <td className="py-3 text-gray-600">{source.topic}</td>
                    <td className="py-3">
                      <span className={source.enabled ? 'text-green-600' : 'text-gray-500'}>
                        {source.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="py-3 text-sm">{formatHealth(source)}</td>
                    <td className="py-3">
                      <div className="flex gap-2">
                        <Button size="sm" variant="secondary" onClick={() => handleOpenEdit(source)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => handleToggleEnabled(source)}>
                          {source.enabled ? 'Disable' : 'Enable'}
                        </Button>
                        <Button size="sm" variant="danger" onClick={() => handleDelete(source)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <CuratedSourceFormModal
        isOpen={isFormOpen}
        source={formTarget}
        onClose={() => setIsFormOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  )
}

export default AdminCuratedSourcesPage
